// Spray tank-mix calculator (mig 626). Pure functions — no I/O, easy to test.
//
// Turns a stored recipe ratio + the sprayer tank size + number of tanks into
// real numbers: total water for the job, total chemical, per-tank chemical,
// and the stock-decrement quantity (converted to the chemical's stock unit).
// Three dosing bases, chosen by the line's water/denominator unit:
//   • per water volume — "300 g / 100 L"  → total = chemAmount × (totalWater / 100)
//   • per hectare       — "2 L / ha"       → total = chemAmount × totalHectares
//   • per tank          — "6 L / tank"     → total = chemAmount × tanks (lane 2a)
//
// Used by SprayRecords (record), SprayTasks (plan), MySprayTasks (completion).

export type Dimension = 'volume' | 'mass' | 'area' | 'unknown'

export function dimensionOf(unit: string | null | undefined): Dimension {
  switch ((unit ?? '').trim().toLowerCase()) {
    case 'ml': case 'l': return 'volume'
    case 'g':  case 'kg': return 'mass'
    case 'ha': return 'area'
    default:   return 'unknown'
  }
}

// Base units: volume = mL, mass = g, area = ha.
const TO_BASE: Record<string, number> = { ml: 1, l: 1000, g: 1, kg: 1000, ha: 1 }

// Same-dimension conversion ONLY. Returns null across dimensions — we never
// guess density (mL ↔ g) or pack weight. f===t short-circuits.
export function convert(amount: number, from: string, to: string): number | null {
  if (!Number.isFinite(amount)) return null
  const f = (from ?? '').trim().toLowerCase()
  const t = (to ?? '').trim().toLowerCase()
  if (f === t) return amount
  if (dimensionOf(from) === 'unknown' || dimensionOf(from) !== dimensionOf(to)) return null
  const fb = TO_BASE[f], tb = TO_BASE[t]
  if (fb == null || tb == null) return null
  return (amount * fb) / tb
}

// Round a computed quantity for display / field auto-fill (drops float noise).
export function roundQty(n: number, dp = 2): number {
  if (!Number.isFinite(n)) return n   // never turn NaN/Infinity into a fake number
  const f = 10 ** dp
  return Math.round((n + Number.EPSILON) * f) / f
}

export interface MixContext {
  tankSizeL:    number | null  // sprayer tank capacity (litres)
  tankCount:    number | null  // number of tank-loads for the job
  waterRateLph: number | null  // spray volume per hectare (L/ha) — gives the ratio
  totalHaSum:   number | null  // sum of target blocks' hectares (suggested tanks / area)
}

export function totalWaterLitres(ctx: MixContext): number | null {
  const { tankSizeL: size, tankCount: count } = ctx
  if (size == null || count == null) return null
  if (!Number.isFinite(size) || !Number.isFinite(count) || size <= 0 || count <= 0) return null
  return size * count
}

// Hectares one tank covers = tank size ÷ water rate per ha. This is the "ratio"
// on a paper spray sheet (e.g. 1000 L tank ÷ 630 L/ha = 1.59 ha/tank). It's
// what turns a per-hectare rate into a per-tank amount: perTank = rate/ha × ratio.
export function ratioHaPerTank(ctx: MixContext): number | null {
  const t = ctx.tankSizeL, w = ctx.waterRateLph
  if (t == null || w == null || !Number.isFinite(t) || !Number.isFinite(w) || t <= 0 || w <= 0) return null
  return t / w
}

export interface MixLineInput {
  chemAmount:  string | number
  chemUnit:    string
  waterAmount: string | number
  waterUnit:   string
  stockUnit?:  string | null  // chemical's default stock unit, for auto-fill conversion
}

export interface MixLineResult {
  basis:             'volume' | 'area' | 'tank' | null  // null = nothing computable yet
  totalWaterL:       number | null
  ratio:             number | null   // ha per tank (tank ÷ water-rate); null if water rate unset
  totalHa:           number | null   // area the job's tanks cover (ratio × tanks)
  perTankChem:       number | null   // in chemUnit
  totalChem:         number | null   // in chemUnit
  totalChemUnit:     string
  autoFillQty:       number | null   // suggested quantity_used (in autoFillUnit)
  autoFillUnit:      string | null
  converted:         boolean         // true if chemUnit → stockUnit conversion applied
  dimensionMismatch: boolean         // true if stock unit is a different dimension (can't convert)
}

function num(v: string | number): number {
  return typeof v === 'number' ? v : parseFloat(v)
}

export function computeMixLine(line: MixLineInput, ctx: MixContext): MixLineResult {
  const totalWaterL = totalWaterLitres(ctx)
  const ratio = ratioHaPerTank(ctx)
  const totalHa = (ratio != null && ctx.tankCount != null && ctx.tankCount > 0) ? ratio * ctx.tankCount : null
  const tanks = (ctx.tankCount != null && Number.isFinite(ctx.tankCount) && ctx.tankCount > 0) ? ctx.tankCount : null
  const empty: MixLineResult = {
    basis: null, totalWaterL, ratio, totalHa, perTankChem: null, totalChem: null,
    totalChemUnit: line.chemUnit, autoFillQty: null, autoFillUnit: null,
    converted: false, dimensionMismatch: false,
  }

  const chemAmount = num(line.chemAmount)
  if (!Number.isFinite(chemAmount) || chemAmount <= 0) return empty

  const waterUnit = (line.waterUnit ?? '').trim()
  let basis: 'volume' | 'area' | 'tank'
  // Per-tank chemical is the foundation; the job total is just per-tank × tanks
  // (true for BOTH bases — N identical tank-loads).
  let perTankChem: number | null = null

  if (waterUnit.toLowerCase() === 'tank') {
    // Per tank — the figure IS the per-tank amount, whatever the tank's size.
    basis = 'tank'
    perTankChem = chemAmount
  } else if (waterUnit.toLowerCase() === 'ha') {
    basis = 'area'
    const wa = num(line.waterAmount)
    const denomHa = Number.isFinite(wa) && wa > 0 ? wa : 1   // blank water amount = per 1 ha
    const ratePerHa = chemAmount / denomHa
    // Per-hectare rate → per-tank via the ratio (ha covered per tank). Needs
    // both the tank size and the water rate (L/ha) — else we can't bridge
    // hectares to a tank, so leave it blank rather than guess.
    if (ratio != null) perTankChem = ratePerHa * ratio
  } else {
    basis = 'volume'
    const wa = num(line.waterAmount)
    const denomL = waterUnit.toLowerCase() === 'ml' ? wa / 1000 : wa  // ratio denominator, in L
    // Per-volume (e.g. per 100 L) → per-tank via the tank's own volume.
    if (Number.isFinite(denomL) && denomL > 0 && ctx.tankSizeL != null && ctx.tankSizeL > 0) {
      perTankChem = chemAmount * (ctx.tankSizeL / denomL)
    }
  }

  let totalChem: number | null = (perTankChem != null && tanks != null) ? perTankChem * tanks : null

  // Guard against any non-finite slip (e.g. Infinity in a numeric field) so we
  // never auto-fill a bogus quantity that would corrupt a stock decrement.
  if (totalChem != null && !Number.isFinite(totalChem)) totalChem = null
  if (perTankChem != null && !Number.isFinite(perTankChem)) perTankChem = null

  // Auto-fill quantity_used: convert the job total to the chemical's stock unit
  // when same dimension; otherwise keep chemUnit and flag the mismatch.
  let autoFillQty: number | null = null
  let autoFillUnit: string | null = null
  let converted = false
  let dimensionMismatch = false
  if (totalChem != null) {
    const stock = line.stockUnit?.trim()
    if (stock && dimensionOf(stock) !== 'unknown' && dimensionOf(stock) === dimensionOf(line.chemUnit)) {
      autoFillQty = convert(totalChem, line.chemUnit, stock)
      autoFillUnit = stock
      converted = stock !== line.chemUnit
    } else if (stock && dimensionOf(stock) !== dimensionOf(line.chemUnit)) {
      // Rate unit and stock unit are different physical dimensions (e.g. mL vs
      // kg). We can't convert without density, and decrementing stock in the
      // wrong unit would corrupt the balance — so DON'T auto-fill. The total is
      // still returned for an informational readout; the operator enters the
      // stock quantity by hand.
      dimensionMismatch = true
      autoFillQty = null
      autoFillUnit = line.chemUnit
    } else {
      // No stock unit to target (free-text line, or a chemical with no default
      // unit) — fill in the rate's own unit. Free-text lines don't decrement.
      autoFillQty = totalChem
      autoFillUnit = line.chemUnit
    }
  }

  return {
    basis, totalWaterL, ratio, totalHa, perTankChem, totalChem, totalChemUnit: line.chemUnit,
    autoFillQty, autoFillUnit, converted, dimensionMismatch,
  }
}

// Collapse a computed line into the two numbers a readout shows: per-tank and
// job-total, in ONE coherent unit. When a stock-unit conversion changed the
// total's unit (autoFillUnit), the per-tank figure is converted to match so the
// line reads consistently ("X g/tank · total Y g"). Returns null when nothing's
// computable yet (no tank size / rate). Shared by the completion readout and the
// pre-spray tank-mix sheet so both show identical figures.
export function mixLineDisplay(
  res: MixLineResult,
): { perTank: number | null; total: number; unit: string; dimensionMismatch: boolean } | null {
  if (res.totalChem == null) return null
  const unit  = res.autoFillUnit ?? res.totalChemUnit
  const total = res.autoFillQty != null ? res.autoFillQty : res.totalChem
  const perTank = res.perTankChem == null
    ? null
    : (unit !== res.totalChemUnit ? (convert(res.perTankChem, res.totalChemUnit, unit) ?? res.perTankChem) : res.perTankChem)
  return { perTank, total, unit, dimensionMismatch: res.dimensionMismatch }
}
